export const supportedLocales = ["en", "es"] as const;

export type Locale = (typeof supportedLocales)[number];

export interface FeatureCopy {
  title: string;
  body: string;
}

interface SectionLead {
  eyebrow: string;
  title: string;
  body: string;
  imageAlt: string;
}

interface FeatureSection<FeatureKey extends string> extends SectionLead {
  features: Record<FeatureKey, FeatureCopy>;
}

type CommunityFeatureKey = "circles" | "petMatch" | "messaging" | "events";
type PetCareFeatureKey = "profile" | "petId" | "sharedOwnership" | "records";
type FindCareFeatureKey = "places" | "services" | "reviews" | "booking";
type DiscoverFeatureKey = "adoption" | "events" | "nearby";
type BusinessFeatureKey =
  | "organizations"
  | "locations"
  | "services"
  | "team"
  | "schedules"
  | "appointments"
  | "events"
  | "adoptionListings";
type BusinessStepKey = "create" | "shape" | "coordinate";

interface HeroCopy {
  eyebrow: string;
  title: string;
  body: string;
  primaryCta: string;
  secondaryCta: string;
  imageAlt: string;
}

export interface MarketingCopy {
  meta: {
    homeTitle: string;
    homeDescription: string;
    businessTitle: string;
    businessDescription: string;
    notFoundTitle: string;
  };
  brand: {
    name: string;
    logoAlt: string;
    tagline: string;
  };
  nav: {
    home: string;
    community: string;
    petCare: string;
    findCare: string;
    discover: string;
    business: string;
  };
  controls: {
    menu: string;
    language: string;
    english: string;
    spanish: string;
    switchToEnglish: string;
    switchToSpanish: string;
    theme: string;
    light: string;
    dark: string;
    system: string;
    switchToLight: string;
    switchToDark: string;
    settings: string;
    signIn: string;
  };
  common: {
    getTheApp: string;
    appStore: string;
    appStoreBadgeAlt: string;
    exploreFeatures: string;
    learnMore: string;
    forBusinesses: string;
    backToHome: string;
  };
  home: {
    hero: HeroCopy;
    community: FeatureSection<CommunityFeatureKey>;
    petCare: FeatureSection<PetCareFeatureKey>;
    findCare: FeatureSection<FindCareFeatureKey>;
    discover: FeatureSection<DiscoverFeatureKey>;
    download: {
      eyebrow: string;
      title: string;
      body: string;
      cta: string;
      imageAlt: string;
    };
    businessTeaser: {
      eyebrow: string;
      title: string;
      body: string;
      cta: string;
      imageAlt: string;
    };
  };
  business: {
    hero: HeroCopy;
    overview: {
      eyebrow: string;
      title: string;
      body: string;
      audience: string;
    };
    capabilities: {
      eyebrow: string;
      title: string;
      body: string;
      features: Record<BusinessFeatureKey, FeatureCopy>;
    };
    gettingStarted: {
      eyebrow: string;
      title: string;
      body: string;
      steps: Record<BusinessStepKey, FeatureCopy>;
    };
    cta: {
      eyebrow: string;
      title: string;
      body: string;
      button: string;
      note: string;
    };
  };
  footer: {
    tagline: string;
    exploreTitle: string;
    companyTitle: string;
    legalTitle: string;
    about: string;
    contact: string;
    privacy: string;
    terms: string;
    cookies: string;
    businessPrompt: string;
    businessLink: string;
    copyright: string;
  };
  accessibility: {
    skipToContent: string;
    primaryNavigation: string;
    footerNavigation: string;
    openMenu: string;
    closeMenu: string;
    homeLink: string;
    scrollToTop: string;
    languageSwitcher: string;
    themeSwitcher: string;
    externalLink: string;
  };
  notFound: {
    eyebrow: string;
    title: string;
    body: string;
    homeCta: string;
    businessCta: string;
  };
}

export const marketingCopy: Record<Locale, MarketingCopy> = {
  en: {
    meta: {
      homeTitle: "PetCollab | Pet life is better together",
      homeDescription:
        "Meet pet people, find compatible friends for your pet, keep care organized, and discover nearby support in one connected app.",
      businessTitle: "PetCollab for Business | Stay close to your pet community",
      businessDescription:
        "Organize locations, services, teams, schedules, appointments, events, and adoption listings from PetCollab.",
      notFoundTitle: "Page not found | PetCollab",
    },
    brand: {
      name: "PetCollab",
      logoAlt: "PetCollab home",
      tagline: "Connection, care, and discovery for life with pets.",
    },
    nav: {
      home: "Home",
      community: "Community",
      petCare: "Care together",
      findCare: "Find care",
      discover: "Discover",
      business: "For businesses",
    },
    controls: {
      menu: "Menu",
      language: "Language",
      english: "English",
      spanish: "Spanish",
      switchToEnglish: "Switch to English",
      switchToSpanish: "Switch to Spanish",
      theme: "Color theme",
      light: "Light",
      dark: "Dark",
      system: "Use device setting",
      switchToLight: "Switch to light theme",
      switchToDark: "Switch to dark theme",
      settings: "Settings",
      signIn: "Sign in",
    },
    common: {
      getTheApp: "Get the app",
      appStore: "Open in the App Store",
      appStoreBadgeAlt: "Download PetCollab on the App Store",
      exploreFeatures: "Explore the features",
      learnMore: "Learn more",
      forBusinesses: "PetCollab for business",
      backToHome: "Back to home",
    },
    home: {
      hero: {
        eyebrow: "Life with pets, shared",
        title: "Where pet people find their circle.",
        body:
          "Meet nearby pet families, find compatible friends for your pet, and keep everyday care moving together—from one warm, connected place.",
        primaryCta: "Get PetCollab",
        secondaryCta: "See what’s inside",
        imageAlt: "A pet parent reading beside her dog in a cozy sunlit room",
      },
      community: {
        eyebrow: "Connection comes first",
        title: "A community for every way of sharing life with pets.",
        body:
          "Find your people, make room for new friendships, and turn familiar names into familiar faces—for you and your pet.",
        imageAlt: "Pet parents and their dogs gathering together in a sunny park",
        features: {
          circles: {
            title: "Circles",
            body:
              "Join public communities built around shared interests or nearby life, or create a space of your own.",
          },
          petMatch: {
            title: "Pet Match",
            body:
              "Set preferences for your pet, discover compatible friends, and start a conversation when it feels right.",
          },
          messaging: {
            title: "Messaging",
            body:
              "Keep conversations close, from a new connection to the next walk, meetup, or playdate.",
          },
          events: {
            title: "Community events",
            body:
              "See what your circles have planned and find more reasons to spend good time together.",
          },
        },
      },
      petCare: {
        eyebrow: "Care, kept in sync",
        title: "One pet story. Everyone who cares for them.",
        body:
          "Bring the details that shape your pet’s life into one thoughtful profile, so shared care feels a little less scattered.",
        imageAlt: "A pet parent checking care details while sitting beside their dog at home",
        features: {
          profile: {
            title: "A profile that feels like them",
            body:
              "Keep their basics, personality, characteristics, and favorite photos together in one place.",
          },
          petId: {
            title: "Pet ID",
            body:
              "Keep identifying details within reach when you need to quickly share who your pet is.",
          },
          sharedOwnership: {
            title: "Shared ownership",
            body:
              "Add the people who share responsibility for a pet, so important context stays with the whole care team.",
          },
          records: {
            title: "Records and medications",
            body:
              "Organize health records, vaccinations, and medication details as part of their ongoing story.",
          },
        },
      },
      findCare: {
        eyebrow: "Support around the corner",
        title: "From “who can help?” to a plan you can feel good about.",
        body:
          "Explore nearby pet places and services, learn from community experiences, and make an appointment when you are ready.",
        imageAlt: "A groomer carefully trimming a relaxed dog's coat",
        features: {
          places: {
            title: "Pet-friendly places",
            body:
              "Discover nearby locations made for pets and the people who bring them along.",
          },
          services: {
            title: "Services for real life",
            body:
              "Browse the care available at each location and find an option that fits your pet’s needs.",
          },
          reviews: {
            title: "Community reviews",
            body:
              "Read experiences shared by other pet people before deciding what feels right for you.",
          },
          booking: {
            title: "Appointment booking",
            body:
              "Check the details, choose an available time, and keep upcoming care easy to revisit.",
          },
        },
      },
      discover: {
        eyebrow: "More to discover",
        title: "Follow your curiosity. You may meet a new companion along the way.",
        body:
          "Browse adoption stories, discover gatherings, and see more of the pet-friendly world taking shape around you.",
        imageAlt: "A family meeting an adoptable dog during a relaxed community event",
        features: {
          adoption: {
            title: "Adoption listings",
            body:
              "Meet pets looking for a home and learn about their personality, needs, and story.",
          },
          events: {
            title: "Events worth stepping out for",
            body:
              "Find pet-centered gatherings nearby and make a little more room for shared experiences.",
          },
          nearby: {
            title: "Everyday discovery",
            body:
              "Explore places, people, and possibilities near you whenever the day calls for something new.",
          },
        },
      },
      download: {
        eyebrow: "Take your circle with you",
        title: "The everyday parts of pet life belong together.",
        body:
          "Download PetCollab for iPhone to create your pet’s profile, meet the community, and start exploring nearby.",
        cta: "Download on the App Store",
        imageAlt: "A pet parent smiling at their phone while their dog rests nearby",
      },
      businessTeaser: {
        eyebrow: "Are you a pet business?",
        title: "Make it easier for local pet families to find and plan with you.",
        body:
          "Bring your locations, services, team, and appointments into the same community your customers use to connect and care.",
        cta: "Explore PetCollab for business",
        imageAlt: "A groomer caring for a relaxed dog in a bright studio",
      },
    },
    business: {
      hero: {
        eyebrow: "PetCollab for business",
        title: "Run the pet-care side of your day in one connected place.",
        body:
          "Create your organization, shape how each location works, and stay on top of the services and appointments your community depends on.",
        primaryCta: "Get PetCollab",
        secondaryCta: "Explore business tools",
        imageAlt: "A pet groomer carefully trimming a relaxed dog's coat",
      },
      overview: {
        eyebrow: "Built around your work",
        title: "A clearer home for the details behind good pet care.",
        body:
          "PetCollab helps you present your business to pet families while keeping the practical pieces of daily service organized in the app.",
        audience:
          "For independent providers, multi-location teams, rescues, shelters, and other pet-focused organizations.",
      },
      capabilities: {
        eyebrow: "Business essentials",
        title: "Set up the way you serve your community.",
        body:
          "Start with the tools that fit your organization today and keep the rest close as your work grows.",
        features: {
          organizations: {
            title: "Organizations",
            body:
              "Create a business profile that brings your identity, contact details, and offerings together.",
          },
          locations: {
            title: "Locations",
            body:
              "Add and manage the places where your team serves pet families.",
          },
          services: {
            title: "Services",
            body:
              "Describe what you offer and connect each service to the locations where it is available.",
          },
          team: {
            title: "Team",
            body:
              "Add employees and connect them with the right locations and responsibilities.",
          },
          schedules: {
            title: "Schedules",
            body:
              "Set working hours and service availability so pet families can plan ahead.",
          },
          appointments: {
            title: "Appointments",
            body:
              "Review appointment details and keep upcoming bookings visible to your team.",
          },
          events: {
            title: "Events",
            body:
              "Publish pet-centered events and share the details with your community.",
          },
          adoptionListings: {
            title: "Adoption listings",
            body:
              "Share pets looking for homes with families discovering them in PetCollab.",
          },
        },
      },
      gettingStarted: {
        eyebrow: "Start in the app",
        title: "Build your presence one useful detail at a time.",
        body:
          "PetCollab guides the setup while leaving room for the way your organization already works.",
        steps: {
          create: {
            title: "Create your organization",
            body:
              "Add the core details pet families need to understand who you are and how to reach you.",
          },
          shape: {
            title: "Shape your offering",
            body:
              "Connect locations, services, team members, schedules, events, or adoption listings as they apply to you.",
          },
          coordinate: {
            title: "Keep the day moving",
            body:
              "Return to the app to manage appointments and update the information your community sees.",
          },
        },
      },
      cta: {
        eyebrow: "Ready when you are",
        title: "Meet pet families where connection already begins.",
        body:
          "Download PetCollab on iPhone and start creating your business presence from the app.",
        button: "Open PetCollab in the App Store",
        note: "Business tools are available in the PetCollab app. Available features may depend on your organization setup and location.",
      },
    },
    footer: {
      tagline: "A warmer way to connect, care, and discover with pets.",
      exploreTitle: "Explore",
      companyTitle: "PetCollab",
      legalTitle: "Legal",
      about: "About",
      contact: "Contact",
      privacy: "Privacy",
      terms: "Terms",
      cookies: "Cookies",
      businessPrompt: "Care for pets professionally?",
      businessLink: "See PetCollab for business",
      copyright: "© PetCollab. All rights reserved.",
    },
    accessibility: {
      skipToContent: "Skip to main content",
      primaryNavigation: "Primary navigation",
      footerNavigation: "Footer navigation",
      openMenu: "Open navigation menu",
      closeMenu: "Close navigation menu",
      homeLink: "PetCollab home",
      scrollToTop: "Scroll back to the top",
      languageSwitcher: "Choose website language",
      themeSwitcher: "Choose color theme",
      externalLink: "Opens in a new tab",
    },
    notFound: {
      eyebrow: "A little off the trail",
      title: "We couldn’t sniff out that page.",
      body:
        "The link may have moved, or the path may be taking the scenic route. Let’s get you somewhere familiar.",
      homeCta: "Return home",
      businessCta: "Visit the business page",
    },
  },
  es: {
    meta: {
      homeTitle: "PetCollab | La vida con mascotas se disfruta mejor en compañía",
      homeDescription:
        "Conoce a otras personas con mascotas, encuentra amistades compatibles, organiza sus cuidados y descubre apoyo cercano en una sola app.",
      businessTitle: "PetCollab para negocios | Cerca de tu comunidad de mascotas",
      businessDescription:
        "Organiza ubicaciones, servicios, equipos, horarios, citas, eventos y anuncios de adopción desde PetCollab.",
      notFoundTitle: "Página no encontrada | PetCollab",
    },
    brand: {
      name: "PetCollab",
      logoAlt: "Inicio de PetCollab",
      tagline: "Conexión, cuidado y descubrimiento para compartir la vida con tus mascotas.",
    },
    nav: {
      home: "Inicio",
      community: "Comunidad",
      petCare: "Cuidar en equipo",
      findCare: "Encontrar cuidados",
      discover: "Descubrir",
      business: "Para negocios",
    },
    controls: {
      menu: "Menú",
      language: "Idioma",
      english: "Inglés",
      spanish: "Español",
      switchToEnglish: "Cambiar a inglés",
      switchToSpanish: "Cambiar a español",
      theme: "Tema de color",
      light: "Claro",
      dark: "Oscuro",
      system: "Usar la configuración del dispositivo",
      switchToLight: "Cambiar al tema claro",
      switchToDark: "Cambiar al tema oscuro",
      settings: "Configuración",
      signIn: "Iniciar sesión",
    },
    common: {
      getTheApp: "Descargar la app",
      appStore: "Abrir en el App Store",
      appStoreBadgeAlt: "Descarga PetCollab en el App Store",
      exploreFeatures: "Explorar las funciones",
      learnMore: "Conocer más",
      forBusinesses: "PetCollab para negocios",
      backToHome: "Volver al inicio",
    },
    home: {
      hero: {
        eyebrow: "La vida con mascotas, compartida",
        title: "Donde quienes aman a sus mascotas encuentran su círculo.",
        body:
          "Conoce a otras familias con mascotas, encuentra amistades compatibles y coordina los cuidados del día a día desde un espacio cálido y conectado.",
        primaryCta: "Descargar PetCollab",
        secondaryCta: "Descubre lo que hay dentro",
        imageAlt: "Una persona leyendo junto a su perro en una habitación cálida y soleada",
      },
      community: {
        eyebrow: "La conexión va primero",
        title: "Una comunidad para cada forma de compartir la vida con mascotas.",
        body:
          "Encuentra a tu gente, abre espacio para nuevas amistades y convierte nombres conocidos en caras conocidas para ti y tu mascota.",
        imageAlt: "Personas con sus perros reunidas en un parque soleado",
        features: {
          circles: {
            title: "Círculos",
            body:
              "Únete a comunidades públicas por intereses compartidos o cercanía, o crea tu propio espacio.",
          },
          petMatch: {
            title: "Pet Match",
            body:
              "Define las preferencias de tu mascota, descubre amistades compatibles y comienza una conversación cuando se sienta bien.",
          },
          messaging: {
            title: "Mensajes",
            body:
              "Mantén la conversación cerca, desde el primer saludo hasta el próximo paseo, encuentro o día de juegos.",
          },
          events: {
            title: "Eventos de la comunidad",
            body:
              "Descubre lo que planean tus círculos y encuentra más motivos para pasar buenos momentos en compañía.",
          },
        },
      },
      petCare: {
        eyebrow: "Cuidados en sintonía",
        title: "Una sola historia para tu mascota. Al alcance de quienes la cuidan.",
        body:
          "Reúne los detalles que forman parte de su vida en un perfil pensado con cariño, para que el cuidado compartido se sienta menos disperso.",
        imageAlt: "Una persona revisando información de cuidados mientras está sentada junto a su perro en casa",
        features: {
          profile: {
            title: "Un perfil que se parece a tu mascota",
            body:
              "Guarda sus datos, personalidad, características y fotos favoritas en un mismo lugar.",
          },
          petId: {
            title: "Identificación de mascota",
            body:
              "Ten sus datos de identificación a mano cuando necesites compartir rápidamente quién es.",
          },
          sharedOwnership: {
            title: "Cuidado compartido",
            body:
              "Añade a las personas que comparten la responsabilidad de una mascota para que todo el equipo tenga el contexto importante.",
          },
          records: {
            title: "Registros y medicamentos",
            body:
              "Organiza registros de salud, vacunas y medicamentos como parte de su historia continua.",
          },
        },
      },
      findCare: {
        eyebrow: "Apoyo a la vuelta de la esquina",
        title: "De “¿quién puede ayudar?” a un plan que te da tranquilidad.",
        body:
          "Explora lugares y servicios cercanos, conoce las experiencias de la comunidad y agenda una cita cuando estés listo.",
        imageAlt: "Una profesional de peluquería canina recortando con cuidado el pelo de un perro relajado",
        features: {
          places: {
            title: "Lugares que reciben mascotas",
            body:
              "Descubre ubicaciones cercanas pensadas para las mascotas y las personas que las acompañan.",
          },
          services: {
            title: "Servicios para la vida real",
            body:
              "Explora los cuidados disponibles en cada lugar y encuentra una opción para las necesidades de tu mascota.",
          },
          reviews: {
            title: "Reseñas de la comunidad",
            body:
              "Conoce experiencias compartidas por otras personas antes de decidir qué funciona mejor para ustedes.",
          },
          booking: {
            title: "Reserva de citas",
            body:
              "Revisa los detalles, elige un horario disponible y vuelve a consultar fácilmente los próximos cuidados.",
          },
        },
      },
      discover: {
        eyebrow: "Hay más por descubrir",
        title: "Sigue tu curiosidad. Tal vez encuentres un nuevo compañero en el camino.",
        body:
          "Explora historias de adopción, descubre encuentros y conoce mejor el mundo pet-friendly que crece a tu alrededor.",
        imageAlt: "Una familia conociendo a un perro en adopción durante un evento de la comunidad",
        features: {
          adoption: {
            title: "Anuncios de adopción",
            body:
              "Conoce mascotas que buscan un hogar y aprende sobre su personalidad, necesidades e historia.",
          },
          events: {
            title: "Eventos que invitan a salir",
            body:
              "Encuentra encuentros para mascotas cerca de ti y abre espacio para más experiencias compartidas.",
          },
          nearby: {
            title: "Descubrimientos cotidianos",
            body:
              "Explora lugares, personas y posibilidades cercanas cuando el día pida algo nuevo.",
          },
        },
      },
      download: {
        eyebrow: "Lleva tu círculo contigo",
        title: "Los momentos cotidianos de la vida con mascotas merecen estar conectados.",
        body:
          "Descarga PetCollab para iPhone, crea el perfil de tu mascota, conoce la comunidad y empieza a explorar lo que hay cerca.",
        cta: "Descargar en el App Store",
        imageAlt: "Una persona sonriendo al mirar su teléfono mientras su perro descansa cerca",
      },
      businessTeaser: {
        eyebrow: "¿Tienes un negocio de mascotas?",
        title: "Facilita que las familias de tu comunidad te encuentren y hagan planes contigo.",
        body:
          "Reúne tus ubicaciones, servicios, equipo y citas dentro de la misma comunidad que tus clientes usan para conectar y cuidar.",
        cta: "Explora PetCollab para negocios",
        imageAlt: "Una profesional cuidando a un perro relajado en un estudio luminoso",
      },
    },
    business: {
      hero: {
        eyebrow: "PetCollab para negocios",
        title: "Organiza el día a día de tu negocio de mascotas en un solo lugar conectado.",
        body:
          "Crea tu organización, define cómo funciona cada ubicación y mantén a mano los servicios y citas que necesita tu comunidad.",
        primaryCta: "Descargar PetCollab",
        secondaryCta: "Explorar herramientas para negocios",
        imageAlt: "Una profesional de peluquería canina recortando con cuidado el pelo de un perro relajado",
      },
      overview: {
        eyebrow: "Creado alrededor de tu trabajo",
        title: "Un lugar más claro para los detalles detrás de un buen cuidado.",
        body:
          "PetCollab te ayuda a presentar tu negocio ante las familias con mascotas y a organizar en la app las partes prácticas del servicio diario.",
        audience:
          "Para profesionales independientes, equipos con varias ubicaciones, organizaciones de rescate, refugios y otros negocios dedicados a las mascotas.",
      },
      capabilities: {
        eyebrow: "Esenciales para tu negocio",
        title: "Configura la forma en que sirves a tu comunidad.",
        body:
          "Empieza con las herramientas que necesita tu organización hoy y mantén las demás cerca a medida que tu trabajo crece.",
        features: {
          organizations: {
            title: "Organizaciones",
            body:
              "Crea un perfil que reúna la identidad, los datos de contacto y la oferta de tu negocio.",
          },
          locations: {
            title: "Ubicaciones",
            body:
              "Añade y administra los lugares donde tu equipo atiende a las familias con mascotas.",
          },
          services: {
            title: "Servicios",
            body:
              "Describe lo que ofreces y conecta cada servicio con las ubicaciones donde está disponible.",
          },
          team: {
            title: "Equipo",
            body:
              "Añade empleados y relaciónalos con las ubicaciones y responsabilidades adecuadas.",
          },
          schedules: {
            title: "Horarios",
            body:
              "Define horas de trabajo y disponibilidad de servicios para que las familias puedan planear.",
          },
          appointments: {
            title: "Citas",
            body:
              "Revisa los detalles de cada cita y mantén las próximas reservas visibles para tu equipo.",
          },
          events: {
            title: "Eventos",
            body:
              "Publica eventos para mascotas y comparte los detalles con tu comunidad.",
          },
          adoptionListings: {
            title: "Anuncios de adopción",
            body:
              "Comparte mascotas que buscan hogar con las familias que las descubren en PetCollab.",
          },
        },
      },
      gettingStarted: {
        eyebrow: "Empieza desde la app",
        title: "Construye tu presencia, un detalle útil a la vez.",
        body:
          "PetCollab te acompaña durante la configuración y deja espacio para la forma en que tu organización ya trabaja.",
        steps: {
          create: {
            title: "Crea tu organización",
            body:
              "Añade la información esencial que las familias necesitan para saber quién eres y cómo contactarte.",
          },
          shape: {
            title: "Da forma a tu oferta",
            body:
              "Conecta ubicaciones, servicios, integrantes del equipo, horarios, eventos o anuncios de adopción según tus necesidades.",
          },
          coordinate: {
            title: "Mantén el día en movimiento",
            body:
              "Vuelve a la app para administrar citas y actualizar la información que ve tu comunidad.",
          },
        },
      },
      cta: {
        eyebrow: "Cuando tú quieras",
        title: "Encuentra a las familias donde ya comienzan sus conexiones.",
        body:
          "Descarga PetCollab para iPhone y comienza a crear la presencia de tu negocio desde la app.",
        button: "Abrir PetCollab en el App Store",
        note: "Las herramientas para negocios están disponibles en la app PetCollab. Las funciones pueden variar según la configuración y ubicación de tu organización.",
      },
    },
    footer: {
      tagline: "Una forma más cálida de conectar, cuidar y descubrir con tus mascotas.",
      exploreTitle: "Explorar",
      companyTitle: "PetCollab",
      legalTitle: "Legal",
      about: "Acerca de",
      contact: "Contacto",
      privacy: "Privacidad",
      terms: "Términos",
      cookies: "Cookies",
      businessPrompt: "¿Cuidas mascotas de forma profesional?",
      businessLink: "Conoce PetCollab para negocios",
      copyright: "© PetCollab. Todos los derechos reservados.",
    },
    accessibility: {
      skipToContent: "Saltar al contenido principal",
      primaryNavigation: "Navegación principal",
      footerNavigation: "Navegación del pie de página",
      openMenu: "Abrir el menú de navegación",
      closeMenu: "Cerrar el menú de navegación",
      homeLink: "Inicio de PetCollab",
      scrollToTop: "Volver al inicio de la página",
      languageSwitcher: "Elegir el idioma del sitio",
      themeSwitcher: "Elegir el tema de color",
      externalLink: "Se abre en una pestaña nueva",
    },
    notFound: {
      eyebrow: "Un poco fuera del camino",
      title: "No pudimos olfatear esa página.",
      body:
        "Es posible que el enlace haya cambiado o que la ruta esté dando un paseo. Volvamos a un lugar conocido.",
      homeCta: "Volver al inicio",
      businessCta: "Ir a la página para negocios",
    },
  },
};

export function resolveLocale(value?: string | null): Locale {
  return value?.toLowerCase().startsWith("es") ? "es" : "en";
}

export function getMarketingCopy(value?: string | null): MarketingCopy {
  return marketingCopy[resolveLocale(value)];
}
